import React, { useEffect, useRef, useState } from 'react';
import BulletinRichTextEditor from './BulletinRichTextEditor';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { presignAndUploadBulletinFile, isBulletinUploadConfiguredError } from '../api/bulletinUpload';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import type { BulletinAttachmentPayload, BulletinAttachmentRow, BulletinDetailResponse } from '../constants/bulletin';

const MAX_FILES = 5;

const BulletinEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const postId = id && /^\d+$/.test(id) ? Number(id) : NaN;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [existing, setExisting] = useState<BulletinAttachmentRow[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const initialSnapshot = useRef<{ title: string; body: string; att: string } | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  /** 読み込み時の表示期限モード（どちらのチェックボックスを出すか） */
  const [initialDisplayMode, setInitialDisplayMode] = useState<'unlimited' | 'one_month'>('unlimited');
  const [displayMode, setDisplayMode] = useState<'unlimited' | 'one_month'>('unlimited');

  useEffect(() => {
    if (!Number.isInteger(postId) || postId < 1) {
      setLoadError('投稿 ID が不正です');
      setLoading(false);
      return;
    }
    const run = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await apiClient.get<BulletinDetailResponse>(`/api/bulletin/posts/${postId}`);
        if (!res.data.can_edit) {
          setLoadError('この投稿を編集する権限がありません');
          setLoading(false);
          return;
        }
        const { post, attachments } = res.data;
        setTitle(post.title);
        setBody(post.body);
        setExisting(attachments);
        const im: 'unlimited' | 'one_month' =
          post.display_expires_at && new Date(post.display_expires_at) > new Date()
            ? 'one_month'
            : 'unlimited';
        setInitialDisplayMode(im);
        setDisplayMode(im);
        initialSnapshot.current = {
          title: post.title,
          body: post.body,
          att: JSON.stringify(attachments.map((a) => ({ id: a.id, s3_key: a.s3_key, sort_order: a.sort_order }))),
        };
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          setLoadError('投稿が見つかりません');
        } else {
          setLoadError('投稿の取得に失敗しました');
        }
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [postId]);

  const totalCount = existing.length + newFiles.length;

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const nf = [...newFiles];
    for (let i = 0; i < list.length; i += 1) {
      if (existing.length + nf.length >= MAX_FILES) break;
      nf.push(list[i]);
    }
    setNewFiles(nf);
  };

  const attachmentsDirty = () => {
    if (newFiles.length > 0) return true;
    const cur = JSON.stringify(existing.map((a) => ({ id: a.id, s3_key: a.s3_key, sort_order: a.sort_order })));
    return cur !== initialSnapshot.current?.att;
  };

  const submit = async () => {
    const t = title.trim();
    if (!t) {
      setError('タイトルを入力してください');
      return;
    }
    if (!initialSnapshot.current) return;

    setError('');
    setSaving(true);
    try {
      const payload: {
        title: string;
        body: string;
        attachments?: BulletinAttachmentPayload[];
        display_expires_mode?: 'unlimited' | 'one_month';
      } = { title: t, body };

      if (displayMode !== initialDisplayMode) {
        payload.display_expires_mode = displayMode;
      }

      if (attachmentsDirty()) {
        const uploaded: BulletinAttachmentPayload[] = [];
        for (const f of newFiles) {
          try {
            uploaded.push(await presignAndUploadBulletinFile(f));
          } catch (e: unknown) {
            if (isBulletinUploadConfiguredError(e)) {
              showSnackbar({
                message: 'ファイル添付は現在利用できません。添付を外すか、本文のみ保存してください。',
                severity: 'error',
              });
              setSaving(false);
              return;
            }
            const msg =
              axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
                ? (e.response.data as { error?: string }).error
                : null;
            showSnackbar({ message: msg || 'ファイルのアップロードに失敗しました', severity: 'error' });
            setSaving(false);
            return;
          }
        }
        const kept = existing.map((a) => ({
          s3_key: a.s3_key,
          original_filename: a.original_filename,
          content_type: a.content_type,
          byte_size: a.byte_size,
        }));
        payload.attachments = [...kept, ...uploaded];
      }

      await apiClient.patch(`/api/bulletin/posts/${postId}`, payload);
      showSnackbar({ message: '投稿を保存しました', severity: 'success' });
      navigate(`/bulletin/${postId}`);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      showSnackbar({ message: msg || '投稿の更新に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          maxWidth: 960,
          width: '100%',
          mx: 'auto',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        投稿を編集
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TextField
          label="タイトル"
          fullWidth
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />
        <BulletinRichTextEditor
          label="本文"
          value={body}
          onChange={setBody}
          minRows={8}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
          表示期限
        </Typography>
        {initialDisplayMode === 'unlimited' ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={displayMode === 'one_month'}
                onChange={(_, checked) => setDisplayMode(checked ? 'one_month' : 'unlimited')}
              />
            }
            label="1ヶ月後に非表示にする"
          />
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                checked={displayMode === 'unlimited'}
                onChange={(_, checked) => setDisplayMode(checked ? 'unlimited' : 'one_month')}
              />
            }
            label="無期限にする"
          />
        )}

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          添付（最大 {MAX_FILES} 件）
        </Typography>
        <Button variant="outlined" component="label" size="small" sx={{ mb: 1 }} disabled={totalCount >= MAX_FILES}>
          ファイルを追加
          <input
            type="file"
            hidden
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {existing.map((a) => (
            <Chip
              key={a.id}
              label={a.original_filename}
              onDelete={() => setExisting(existing.filter((x) => x.id !== a.id))}
            />
          ))}
          {newFiles.map((f, i) => (
            <Chip
              key={`new-${f.name}-${i}`}
              label={f.name}
              color="primary"
              variant="outlined"
              onDelete={() => setNewFiles(newFiles.filter((_, j) => j !== i))}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={() => void submit()} disabled={saving}>
            保存する
          </Button>
          <Button variant="text" onClick={() => navigate(`/bulletin/${postId}`)} disabled={saving}>
            キャンセル
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BulletinEdit;
